// Playing audio clips (spec §19.2): one <audio> element for the page, one clip
// at a time, just for this person. The canvas draws the progress from here.
import type { Audio } from '../../shared/types.ts';

export class AudioPlayer {
  /** The clip that's loaded (playing or paused), if any. */
  id: string | null = null;
  playing = false;
  /** Added to asset URLs: `?p=<token>` on a published board. */
  query = '';
  /** Clips recorded or added in this tab, playable from memory before their upload lands. */
  readonly local = new Map<string, string>();
  /** Something changed: playing, paused, ended, moved. */
  onChange: () => void = () => {};
  onError: (message: string) => void = () => {};

  private el: HTMLAudioElement | null = null;
  private duration = 0;
  /** A seek waiting for the browser to find the length (see fixLength). */
  private pendingSeek: number | null = null;

  /** Plays or pauses `o`; another clip that was playing stops. */
  toggle(o: Audio): void {
    if (this.id === o.id && this.el) {
      if (this.el.paused) this.play();
      else this.el.pause();
      return;
    }
    this.load(o);
    this.play();
  }

  /** Jumps to `fraction` of the way through the loaded clip. */
  seek(o: Audio, fraction: number): void {
    if (this.id !== o.id || !this.el) return;
    const t = fraction * this.length();
    if (Number.isFinite(this.el.duration)) this.el.currentTime = t;
    else this.pendingSeek = t;
    this.onChange();
  }

  /** How far through the loaded clip, 0..1. */
  fraction(): number {
    const len = this.length();
    return this.el && len > 0 ? Math.min(1, this.el.currentTime / len) : 0;
  }

  stop(): void {
    if (!this.el) return;
    this.el.pause();
    this.el.removeAttribute('src');
    this.el.load();
    this.id = null;
    this.playing = false;
    this.onChange();
  }

  /** Where a clip's file is: in memory if it was made here, else the server. */
  url(o: Pick<Audio, 'assetHash'>): string {
    return this.local.get(o.assetHash) ?? `/assets/${o.assetHash}/original${this.query}`;
  }

  private load(o: Audio): void {
    const el = (this.el ??= this.create());
    if (!el.canPlayType(o.mime)) {
      this.onError('This browser can’t play this recording.');
      return;
    }
    this.id = o.id;
    this.duration = o.duration;
    this.pendingSeek = null;
    el.src = this.url(o);
  }

  private play(): void {
    this.el?.play().catch((err: unknown) => {
      if ((err as Error).name !== 'AbortError') this.onError('Couldn’t play this recording.');
    });
  }

  /** The browser's idea of the length when it has one; recordings in WebM often don't say. */
  private length(): number {
    const d = this.el?.duration ?? NaN;
    return Number.isFinite(d) && d > 0 ? d : this.duration;
  }

  private create(): HTMLAudioElement {
    const el = document.createElement('audio');
    el.preload = 'auto';
    const changed = () => {
      this.playing = !el.paused && !el.ended;
      this.onChange();
    };
    for (const type of ['play', 'pause', 'ended', 'seeked']) el.addEventListener(type, changed);
    el.addEventListener('ended', () => { el.currentTime = 0; });
    el.addEventListener('loadedmetadata', () => this.fixLength(el));
    el.addEventListener('error', () => {
      if (!this.id) return;
      this.id = null;
      this.playing = false;
      this.onChange();
      this.onError('Couldn’t play this recording.');
    });
    return el;
  }

  /**
   * WebM from a browser's recorder has no length in its header, so the element can't seek
   * until it has read to the end. Asking for a time far past the end makes it do that.
   */
  private fixLength(el: HTMLAudioElement): void {
    if (Number.isFinite(el.duration)) return;
    const back = () => {
      if (!Number.isFinite(el.duration)) return;
      el.removeEventListener('durationchange', back);
      el.currentTime = this.pendingSeek ?? 0;
      this.pendingSeek = null;
    };
    el.addEventListener('durationchange', back);
    el.currentTime = 1e101;
  }
}
